import type { PermitApplicationStatus } from "@/api/permit_applications";
import { PERMIT_APPLICATION_STATUS_LABELS } from "@/api/permit_applications";
import {
  formatWorkflowDeadline,
  isWorkflowOverdue,
  sortByWorkflowDeadline,
  workflowDeadlineSummary,
  workflowDeadlineTone,
} from "@/lib/deadlineDisplay";

export type { PermitApplicationStatus };

export function permitApplicationStatusLabel(status: PermitApplicationStatus): string {
  return PERMIT_APPLICATION_STATUS_LABELS[status];
}

export function permitApplicationStatusPillClass(status: PermitApplicationStatus): string {
  switch (status) {
    case "intake":
      return "bg-slate-100 text-slate-800 dark:bg-slate-800/60 dark:text-slate-200";
    case "under_review":
      return "bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200";
    case "approved":
      return "bg-violet-100 text-violet-800 dark:bg-violet-950/50 dark:text-violet-200";
    case "issued":
      return "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200";
    case "denied":
      return "bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200";
    case "on_hold":
      return "bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200";
    case "closed":
    default:
      return "bg-muted text-muted-foreground";
  }
}

export function formatPermitDeadline(value: string | null): string {
  return formatWorkflowDeadline(value);
}

export function permitDeadlineSummary(value: string | null): string {
  return workflowDeadlineSummary(value);
}

export function isPermitOverdue(value: string | null): boolean {
  return isWorkflowOverdue(value);
}

export function permitDeadlineTone(value: string | null): "default" | "warning" | "muted" {
  return workflowDeadlineTone(value);
}

export const PERMIT_APPLICATION_STATUS_ORDER: PermitApplicationStatus[] = [
  "intake",
  "under_review",
  "approved",
  "issued",
  "denied",
  "on_hold",
  "closed",
];

export function sortPermitApplicationsByDeadline<T extends { deadline_at: string | null }>(
  items: T[],
): T[] {
  return sortByWorkflowDeadline(items);
}

export function pipelineSummaryStats(applications: Array<{ status: PermitApplicationStatus }>) {
  const active = applications.filter((a) => !["issued", "denied", "closed"].includes(a.status));
  return {
    total: applications.length,
    active: active.length,
    underReview: applications.filter((a) => a.status === "under_review").length,
  };
}

export function permitApplicationListMeta(application: {
  application_number: string | null;
  applicant_name: string | null;
  project_address: string | null;
  assigned_to_name: string | null;
}): string {
  return [
    application.application_number,
    application.applicant_name,
    application.project_address,
    application.assigned_to_name ? `Owner: ${application.assigned_to_name}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}
